import { generateObjectUrlFromUnit8Array } from '../utils/file';
import { AppVersionVo } from './app-version-vo';
import { DateVo } from './date-vo';
import { MultiTemplateModel } from './multi-template-model';
import { SingleTemplateModel } from './single-template-model';
import { SuitablePhotosCountVo } from './suitable-photos-count-vo';
import { TemplateSizeCateoryEnum } from './template-size-category-enum';

export abstract class TemplateModel {
  /**
    TemplateModel has detailed information of a template, unlike InTagTemplate which is for
    bulk edit/show templates under a template tag.
  */
  constructor(
    private id: number,
    private author: string,
    private uploaderInfo: string,
    private sourceUrl: string,
    private isVip: boolean,
    private isAnimated: boolean,
    private thumbnailUrl: string,
    private animatedThumbnailUrl: string,
    private sizeCategory: TemplateSizeCateoryEnum,
    private slotsCount: number,
    private creatorId: string | null,
    private createdAt: DateVo,
    private updatedAt: DateVo,
    private isMultipage: boolean,
    private isAdjustable: boolean,
    private suitablePhotosCount: SuitablePhotosCountVo,
    private labels: string[],
    private minimalRequiredVersionIos: AppVersionVo | null,
    private minimalRequiredVersionAndroid: AppVersionVo | null,
    private templateTagIds: number[],
    private shouldRemoveAnimatedThumbnail: boolean,
    private thumbnailBinary: Uint8Array | null,
    private animatedThumbnailBinary: Uint8Array | null,
    private newTemplateTagNames: string[],
    private suitsVideo: boolean,
  ) {}

  abstract isSingleTemplateModel(): this is SingleTemplateModel;

  abstract isMultiTemplateModel(): this is MultiTemplateModel;

  abstract clone(): TemplateModel;

  public getId() {
    return this.id;
  }

  public getAuthor() {
    return this.author;
  }

  public getUploaderInfo() {
    return this.uploaderInfo;
  }

  public getSourceUrl() {
    return this.sourceUrl;
  }

  public getIsVip() {
    return this.isVip;
  }

  public getIsAnimated() {
    return this.isAnimated;
  }

  public getThumbnailUrl() {
    return this.thumbnailUrl;
  }

  public getThumbnailBinary() {
    return this.thumbnailBinary;
  }

  public getDisplayThumbnailUrl() {
    const thumbnailBinary = this.getThumbnailBinary();
    if (!thumbnailBinary) return this.thumbnailUrl;
    return generateObjectUrlFromUnit8Array(thumbnailBinary);
  }

  public getAnimatedThumbnailUrl() {
    return this.animatedThumbnailUrl;
  }

  public getDisplayAnimatedThumbnailUrl() {
    const animatedThumbnailBinary = this.getAnimatedThumbnailBinary();
    if (!animatedThumbnailBinary) return this.animatedThumbnailUrl;
    if (this.shouldRemoveAnimatedThumbnail) return '';
    return generateObjectUrlFromUnit8Array(animatedThumbnailBinary);
  }

  public getAnimatedThumbnailBinary() {
    return this.animatedThumbnailBinary;
  }

  public getSizeCategory() {
    return this.sizeCategory;
  }

  public getSlotsCount() {
    return this.slotsCount;
  }

  public getCreatorId() {
    return this.creatorId;
  }

  public updateCreatorId(creatorId: string | null) {
    this.creatorId = creatorId;
  }

  public getCreatedAt() {
    return this.createdAt;
  }

  public getUpdatedAt() {
    return this.updatedAt;
  }

  public getIsMultipage() {
    return this.isMultipage;
  }

  public getIsAdjustable() {
    return this.isAdjustable;
  }

  public getSuitablePhotosCount() {
    return this.suitablePhotosCount;
  }

  /**
   * Format the suitable photos count that backend needs
   */
  public getFormattedSuitablePhotosCount() {
    return this.suitablePhotosCount.getFormatted();
  }

  public getLabels() {
    return this.labels;
  }

  public getMinimalRequiredVersionIos() {
    return this.minimalRequiredVersionIos;
  }

  public getMinimalRequiredVersionAndroid() {
    return this.minimalRequiredVersionAndroid;
  }

  public getTemplateTagIds() {
    return this.templateTagIds;
  }

  public getNewTemplateTagNames() {
    return this.newTemplateTagNames;
  }

  public getShouldRemoveAnimatedThumbnail() {
    return this.shouldRemoveAnimatedThumbnail;
  }

  public getSuitsVideo() {
    return this.suitsVideo;
  }

  public updateShouldRemoveAnimatedThumbnail(shouldRemoveAnimatedThumbnail: boolean) {
    this.shouldRemoveAnimatedThumbnail = shouldRemoveAnimatedThumbnail;
  }

  public updateThumbnailBinary(thumbnailBinary: Uint8Array | null) {
    this.thumbnailBinary = thumbnailBinary;
  }

  public updateAnimatedThumbnailBinary(animatedThumbnailBinary: Uint8Array | null) {
    this.animatedThumbnailBinary = animatedThumbnailBinary;
  }

  public updateIsAdjustable(isAdjustable: boolean) {
    this.isAdjustable = isAdjustable;
  }

  public updateSuitablePhotosCount(suitablePhotosCount: SuitablePhotosCountVo) {
    this.suitablePhotosCount = suitablePhotosCount;
  }

  public updateTemplateTagIds(templateTagIds: number[]) {
    this.templateTagIds = templateTagIds;
  }

  public updateLabels(labels: string[]) {
    this.labels = labels;
  }

  public updateNewTemplateTagNames(newTemplateTagNames: string[]) {
    this.newTemplateTagNames = newTemplateTagNames;
  }

  public updateSuitsVideo(suitsVideo: boolean) {
    this.suitsVideo = suitsVideo;
  }
}
